import { History, RefreshCw } from "@ews-admin/global-design-system";
import type { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { DoctorAdminActionAuditEntry } from "../../types/doctorAdmin.definition";
import { formatDateTime } from "../../utils/date-format-utils";

/**
 * MED-1523: the "Details" cell used to dump the raw string the API records,
 * e.g. "previousStatus=null, newStatus=TRIAL, startDate=2026-09-30,
 * endDate=2026-10-30". Turn the keys it is known to use into a translated
 * sentence — "Statut : aucun -> Essai, du 30/09/2026 au 30/10/2026" — and fall
 * back to the raw string whenever the shape is not the one below, so a new
 * action type recorded tomorrow still shows something rather than a blank cell.
 */
function parseDetailPairs(details: string): Record<string, string> {
  return Object.fromEntries(
    details
      .split(",")
      .map(part => part.split("="))
      .filter(parts => parts.length === 2)
      .map(([key, value]) => [key.trim(), value.trim()])
  );
}

export function formatAuditDetails(t: TFunction, details: string): string {
  const pairs = parseDetailPairs(details);
  const statusLabel = (value: string | undefined) => {
    if (!value || value === "null") {
      return t("admin.doctors.audit.details.noStatus");
    }
    return t(`doctorLicence.section.statuses.${value}`, {
      defaultValue: value,
    });
  };

  const sentences: string[] = [];
  if ("previousStatus" in pairs || "newStatus" in pairs) {
    sentences.push(
      t("admin.doctors.audit.details.statusChange", {
        previous: statusLabel(pairs.previousStatus),
        next: statusLabel(pairs.newStatus),
      })
    );
  }
  if (pairs.startDate && pairs.endDate) {
    sentences.push(
      t("admin.doctors.audit.details.period", {
        start: formatDateTime(pairs.startDate),
        end: formatDateTime(pairs.endDate),
      })
    );
  }

  return sentences.length > 0 ? sentences.join(", ") : details;
}

interface AuditLogSectionProps {
  auditLog: DoctorAdminActionAuditEntry[];
  loading: boolean;
}

// MED-1255: chronological list of sensitive admin actions taken on a
// doctor's account (currently only free-access grants/extensions, designed
// to cover more action types later). Distinct from the "Grant / Extension
// History" section, which only shows licence grant/extension entries.
export const AuditLogSection: React.FC<AuditLogSectionProps> = ({
  auditLog,
  loading,
}) => {
  const { t } = useTranslation();

  const actionTypeLabel = (actionType: string | null) =>
    actionType
      ? t(`admin.doctors.audit.actionTypes.${actionType}`, {
          defaultValue: actionType,
        })
      : "—";

  return (
    <div className="page-section card-container">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("admin.doctors.audit.title")}
        </h3>
        <History className="w-5 h-5 text-gray-400" />
      </div>

      {loading ? (
        <div className="py-8 text-center">
          <RefreshCw className="mx-auto w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : auditLog.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <p className="text-sm font-medium text-neutral-700">
            {t("admin.doctors.audit.empty")}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.audit.table.actor")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.audit.table.action")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.audit.table.date")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.audit.table.reason")}
                </th>
                <th className="px-4 py-3 text-left font-medium text-neutral-600">
                  {t("admin.doctors.audit.table.details")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {auditLog.map((entry, index) => (
                <tr key={entry.id ?? index} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 text-neutral-800">
                    {entry.actorName ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {actionTypeLabel(entry.actionType)}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {formatDateTime(entry.createdAt, true)}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {entry.reason ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {entry.details ? formatAuditDetails(t, entry.details) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
